<script setup lang="ts">
const { modalType, isModalOpen } = storeToRefs(useLayoutStore());
const { closeModal } = useLayoutStore();
const { parameters } = useParameters();
const { t } = useTexts();

// "Ver meu rollover" leva pra Carteira (tela "wallet" do painel V2,
// ScreenWallet.vue), onde já mora a barra de progresso do rollover
// (UserWalletBonusWallet) — mesmo padrão do botão "Sacar" em
// components/user-v2/CoinsCard.vue.
const { open: openUserV2Screen } = useUserV2Modal();

function viewRollover() {
  closeModal();
  openUserV2Screen('wallet');
}

// Preview dos cards de Saldo Bônus/Saldo BRL (Figma): mesmos dados/formatação
// do UserV2BalancesBar, centralizados em useWallet pra não duplicar a lógica
// de símbolo de moeda nos dois lugares.
const {
  walletCurrencySymbol,
  walletRealFormattedNumber,
  walletBonusFormattedNumber,
} = useWallet();

const isOpen = computed(
  () => isModalOpen.value && modalType.value === 'bonus-rules',
);

const modalCardRef = ref<HTMLElement | null>(null);

const { trapFocus } = useModalA11y(modalCardRef, isOpen, closeModal);

// mesmas fontes de dado do modal antigo (useParameters) — só o texto ao
// redor do número mudou no redesenho, a regra de negócio é a mesma.
const casinoRolloverMultiplier = computed(
  () => parameters.value?.casino?.rollOverMultiplier ?? 0,
);

const casinoExpiresInHours = computed(
  () => parameters.value?.rollover_casino_expires_in ?? 0,
);

const maxWithdrawalMultiplier = computed(
  () => parameters.value?.qtd_withdrawal_bonus || 1,
);

const STATS = computed(() => [
  {
    key: 'casino-bets',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/fire.svg'),
    label: t('account.wallet.bonus_rules_modal.casino_bets'),
    labelFull: t('account.wallet.bonus_rules_modal.casino_bets_full'),
    value: t('account.wallet.bonus_rules_modal.casino_bets_value_short', {
      n: casinoRolloverMultiplier.value,
    }),
    valueFull: t('account.wallet.bonus_rules_modal.casino_bets_value', {
      n: casinoRolloverMultiplier.value,
    }),
  },
  {
    key: 'expiry',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/hourglass.svg'),
    label: t('account.wallet.bonus_rules_modal.casino_expiry_time'),
    labelFull: t('account.wallet.bonus_rules_modal.casino_expiry_time_full'),
    value: `${casinoExpiresInHours.value} ${t('account.wallet.bonus_rules_modal.hour')}`,
    valueFull: `${casinoExpiresInHours.value} ${t('account.wallet.bonus_rules_modal.hour')}`,
  },
  {
    key: 'max-withdrawal',
    icon: getAssetsUrlForCDN('/images/user-v2/bonus-rules/dollar-sign.svg'),
    label: t('account.wallet.bonus_rules_modal.max_bonus_withdrawal'),
    labelFull: t('account.wallet.bonus_rules_modal.max_bonus_withdrawal_full'),
    value: t(
      'account.wallet.bonus_rules_modal.max_bonus_withdrawal_value_short',
      { n: maxWithdrawalMultiplier.value },
    ),
    valueFull: t(
      'account.wallet.bonus_rules_modal.max_bonus_withdrawal_value',
      { n: maxWithdrawalMultiplier.value },
    ),
  },
]);
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="user-wallet-bonus-rules-modal fixed inset-0 z-[1000] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
        @dragstart.prevent
      >
        <!-- o clique fora cai NESTE backdrop (cobre o wrapper inteiro) -->
        <div
          class="modal-backdrop absolute inset-0 bg-black/70"
          @click="closeModal"
        />

        <div
          ref="modalCardRef"
          aria-labelledby="bonus-rules-title"
          aria-modal="true"
          class="modal-card relative flex w-full max-h-[90dvh] flex-col overflow-hidden rounded-t-[22px] bg-v2-default-200 text-left lg:max-w-[500px] lg:rounded-[22px]"
          role="dialog"
          tabindex="-1"
          @keydown="trapFocus"
        >
          <!-- X próprio do Figma (node 24087:70024) -->
          <button
            class="absolute right-[14px] top-[14px] flex size-[32px] items-center justify-center rounded-[8px]"
            type="button"
            @click="closeModal"
          >
            <img
              alt=""
              class="size-[18px]"
              :src="getAssetsUrlForCDN('/images/user-v2/bonus-rules/close.svg')"
            />
          </button>

          <!-- rola sozinho, sem o X junto: viewport baixo (ex.: paisagem
               mobile) não corta mais o topo do card, ver Modal.vue
               (.wrapper/.inner) pro mesmo split -->
          <div
            class="flex min-h-0 flex-col gap-[16px] overflow-y-auto p-[20px]"
          >
            <div class="flex flex-col gap-[5px] pr-[32px]">
              <h6
                id="bonus-rules-title"
                class="text-[19px] font-bold leading-[1.2] text-white"
              >
                {{ $t('account.wallet.bonus_rules_modal.title') }}
              </h6>
            </div>

            <!-- Banner de atenção (Figma: componente "Alert" do heroui,
                 cores exatas do amarelo/yellow, sem token equivalente em
                 user-v2-tokens.scss) -->
            <div
              class="border-solid flex flex-col gap-[7px] rounded-[16px] border border-[#fef9c3] bg-[rgba(254,240,138,0.1)] px-[14px] py-[12px]"
            >
              <p
                class="text-[14px] font-semibold leading-[20px] text-[#fef08a]"
              >
                {{ $t('account.wallet.bonus_rules_modal.attention_title') }}
              </p>
              <p class="text-[12px] font-normal leading-[16px] text-white">
                {{ $t('account.wallet.bonus_rules_modal.attention_example') }}
              </p>
            </div>

            <p class="text-[14px] font-bold leading-[1.4] text-white">
              {{
                $t('account.wallet.bonus_rules_modal.how_to_withdraw_heading')
              }}
            </p>

            <div class="flex flex-col gap-[14px]">
              <!-- cor exata do Figma — sem token equivalente em
                 user-v2-tokens.scss, mesma convenção do gradiente em
                 CoinsCard.vue -->
              <p class="text-[14px] font-medium leading-[1.4] text-[#d4d9e0]">
                {{
                  $t('account.wallet.bonus_rules_modal.how_to_withdraw_text')
                }}
              </p>

              <!-- aviso simplificado num bloco só (o Figma alterna negrito
                   frase a frase dentro da mesma sentença — só "Importante:" e
                   o destaque em amarelo bastam pro aviso continuar claro) -->
              <p class="text-[14px] leading-[1.4] text-white/80">
                <span class="font-semibold text-[#fef08a]">
                  {{ $t('account.wallet.bonus_rules_modal.important_label') }}
                </span>
                {{ $t('account.wallet.bonus_rules_modal.important_text') }}
              </p>
            </div>

            <!-- Preview dos cards de Saldo Bônus/Saldo BRL (Figma): os
                 componentes reais do painel V2 (mesmos dados), não uma
                 recriação estática — mas só PREVIEW, sem interação: `inert`
                 em cada card (não na fileira) desliga o card inteiro
                 (onCardClick), os botões (Usar saldo/Sacar/Depositar) e o
                 próprio ícone ⓘ pra mouse, teclado (Tab não alcança, senão
                 dava pra navegar/reabrir modal de dentro desta modal
                 informativa) e leitor de tela (some da árvore de
                 acessibilidade) — pointer-events-none sozinho só cobria o
                 mouse. `inert` na fileira em vez do card também travaria o
                 scroll horizontal por toque no mobile (o navegador não acha
                 alvo pra arrastar). Largura no wrapper <div>, não direto no
                 CoinsCard (mesmo padrão do BalancesBar.vue) — o CoinsCard já
                 é `flex flex-col` por dentro, então virar TAMBÉM item flex
                 da fileira aqui fora (com w-[…] direto na class dele) faz a
                 altura colapsar pro tamanho do padding só, sem os filhos.
                 `shrink-0`: esta fileira é item flex da coluna rolável do
                 corpo da modal (overflow-y-auto); qualquer overflow não-
                 visible (o overflow-x-auto dela mesma computa overflow-y
                 como auto também, por spec) some com o tamanho mínimo
                 automático do item — sem shrink-0 o flexbox colapsa a
                 fileira pro tamanho do padding, cortando os cards por
                 dentro. -->
            <div
              class="no-scrollbar flex w-full shrink-0 gap-[10px] overflow-x-auto rounded-[20px] bg-v2-default/40 p-[12px]"
            >
              <div
                class="w-[240px] shrink-0"
                inert
              >
                <UserV2CoinsCard
                  :flag="getAssetsUrlForCDN('/images/user-v2/flag-brazil.png')"
                  :prefix="walletCurrencySymbol"
                  show-actions
                  title="Saldo BRL"
                  :value="walletRealFormattedNumber"
                  variant="primary"
                />
              </div>
              <div
                class="w-[240px] shrink-0"
                inert
              >
                <UserV2CoinsCard
                  :prefix="walletCurrencySymbol"
                  show-actions
                  title="Saldo bônus"
                  :value="walletBonusFormattedNumber"
                  variant="bonus"
                />
              </div>
            </div>

            <p class="text-[14px] font-bold leading-[1.4] text-white">
              {{ $t('account.wallet.bonus_rules_modal.subtitle') }}
            </p>

            <div class="flex flex-col gap-[8px] md:flex-row md:gap-[6px]">
              <div
                v-for="stat in STATS"
                :key="stat.key"
                class="flex shrink-0 items-center gap-[12px] rounded-[12px] bg-white/[.04] px-[10px] py-[10px] md:gap-[6px] md:py-[6px]"
              >
                <img
                  alt=""
                  class="size-[32px] shrink-0"
                  :src="stat.icon"
                />
                <div
                  class="flex flex-col gap-[2px] whitespace-nowrap md:items-center"
                >
                  <span class="text-[12px] font-semibold text-white md:hidden">
                    {{ stat.labelFull }}
                  </span>
                  <span
                    class="hidden text-[11px] font-normal text-white/60 md:inline"
                  >
                    {{ stat.label }}
                  </span>
                  <span class="text-[11px] font-medium text-white/80 md:hidden">
                    {{ stat.valueFull }}
                  </span>
                  <span
                    class="hidden text-[11px] font-semibold text-white md:inline"
                  >
                    {{ stat.value }}
                  </span>
                </div>
              </div>
            </div>

            <!-- mesma cor #d4d9e0 do texto acima, sem token equivalente -->
            <p class="text-[14px] font-medium leading-[1.4] text-[#d4d9e0]">
              {{
                $t('account.wallet.bonus_rules_modal.bonus_withdrawal_rules')
              }}
            </p>

            <div class="flex flex-col">
              <div class="flex items-center gap-[11px] py-[14px]">
                <div
                  class="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-white/10"
                >
                  <img
                    alt=""
                    class="size-[18px]"
                    :src="
                      getAssetsUrlForCDN(
                        '/images/user-v2/bonus-rules/slot-machine.svg',
                      )
                    "
                  />
                </div>
                <div class="flex flex-1 flex-col gap-[2px]">
                  <p class="text-[14px] font-bold text-white">
                    {{ $t('account.wallet.bonus_rules_modal.casino') }}
                  </p>
                  <!-- cor exata do Figma — sem token equivalente em
                     user-v2-tokens.scss -->
                  <p class="text-[12.5px] font-medium text-[#9ba6be]">
                    {{
                      $t(
                        'account.wallet.bonus_rules_modal.bonus_withdrawal_casino_rules',
                      )
                    }}
                  </p>
                </div>
              </div>
              <div class="h-px w-full bg-white/[.08]" />
            </div>

            <div class="flex w-full gap-[10px]">
              <button
                class="border-solid flex h-[40px] flex-1 items-center justify-center rounded-full border-2 border-white text-[14px] font-semibold text-white"
                type="button"
                @click="viewRollover"
              >
                {{
                  $t('account.wallet.bonus_rules_modal.view_rollover_button')
                }}
              </button>

              <button
                class="border-solid flex h-[40px] flex-1 items-center justify-center rounded-full border-2 border-white/10 bg-v2-primary text-[14px] font-semibold text-white"
                type="button"
                @click="closeModal"
              >
                {{ $t('account.wallet.bonus_rules_modal.confirm_button') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação de entrada via
// @keyframes/animation (mesma estratégia do UserV2LogoutConfirmModal.vue).
.modal-backdrop {
  animation: bonus-rules-fade 0.18s ease-out;
}

.modal-card {
  animation: bonus-rules-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: bonus-rules-slide-up;
  }
}

@keyframes bonus-rules-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes bonus-rules-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bonus-rules-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
